import { useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

const DESIGNS = [
  {
    slug: "blue-cateye-glazed",
    name: "Blue Cat-Eye Glazed",
    desc: "Glazed ice-blue cat-eye with a celestial orb accent nail and crystal-dusted pearl tips.",
    img: "/uv-0094.jpeg",
  },
  {
    slug: "pink-and-creams",
    name: "Pink & Creams",
    desc: "Blush pink and cream florals with a hand-drawn bow — the romantic set.",
    img: "/uv-0095.jpeg",
  },
  {
    slug: "blue-crystal",
    name: "Blue Crystal",
    desc: "Midnight-sapphire crystals scattered in stardust — the after-dark statement.",
    img: "/uv-0096.jpeg",
  },
  {
    slug: "pink-crystal",
    name: "Pink Crystal",
    desc: "Rose-cut pink crystals that catch the light with every gesture.",
    img: "/uv-0097.jpeg",
  },
  {
    slug: "pastel-cateye-glitter",
    name: "Pastel Cat-Eye Glitter",
    desc: "Pastel cat-eye shades — lilac, mint, butter yellow — each tipped with glittering stardust.",
    img: "/uv-0098.jpeg",
  },
  {
    slug: "green-cateye-glitter",
    name: "Deep Green Cat-Eye",
    desc: "Deep green cat-eye nails edged in fine glitter — jewel-toned and magnetic.",
    img: "/uv-0099.jpeg",
  },
  {
    slug: "swarovski-multicolour",
    name: "Swarovski Spectrum",
    desc: "Multi-colour Swarovski rhinestone crystal nails — a different jewel on every finger.",
    img: "/uv-0100.jpeg",
  },
];

export default function UvCollection({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#26221F]/70 p-4 backdrop-blur-sm md:p-8"
      onClick={onClose}
      data-testid="uv-collection-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Press-On Nails with UV Light collection"
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="max-h-[90vh] w-full max-w-6xl overflow-y-auto border border-[#26221F]/25 bg-[#FAF7F2]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-6 border-b border-[#26221F]/15 bg-[#FAF7F2]/95 px-6 py-6 backdrop-blur md:px-10">
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.4em] text-[#6F665F]">
              Chapter 02 — Cured Under UV
            </p>
            <h3 className="font-display text-3xl tracking-tight md:text-5xl" data-testid="uv-collection-title">
              Press-On Nails <em className="italic text-[#D8AFA4]">with UV Light</em>
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#6F665F]">
              Seven debut designs. Every set cures under the pocket UV light for a
              salon-grade hold that lasts for weeks.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close collection"
            data-testid="uv-close-button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-10 px-6 py-10 sm:grid-cols-2 md:px-10 lg:grid-cols-3">
          {DESIGNS.map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`uv-design-${d.slug}`}
            >
              <div className="group relative overflow-hidden border border-[#26221F]/20">
                <img
                  src={d.img}
                  alt={`${d.name} press-on nails`}
                  loading="lazy"
                  className="w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                  data-testid={`uv-design-image-${d.slug}`}
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-[#FAF7F2]/90 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26221F]">
                  Coming Soon
                </span>
              </div>
              <div className="mt-5 flex items-baseline gap-4">
                <span className="font-display text-3xl italic leading-none text-[#D8AFA4]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h4 className="font-display text-xl tracking-tight" data-testid={`uv-design-title-${d.slug}`}>
                    {d.name}
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-[#6F665F]">{d.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
          <div className="flex flex-col justify-center border border-dashed border-[#26221F]/25 p-8 text-center">
            <p className="font-display text-2xl italic text-[#26221F]">Yours next?</p>
            <p className="mt-3 text-sm leading-relaxed text-[#6F665F]">
              Join the waitlist and be first to choose your set when the debut
              collection drops.
            </p>
            <a
              href="#waitlist"
              onClick={onClose}
              data-testid="uv-waitlist-link"
              className="mx-auto mt-6 rounded-full bg-[#26221F] px-8 py-3.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#D8AFA4] hover:text-[#26221F]"
            >
              Join the Waitlist
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
